import * as React from "react";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { DistributionDividendPreference } from "../../../../../../../Wrap/Constants/Enums/Domain/DistributionDividendPreference";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { MoneyOrPercentageType } from "../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { InstrumentSelection } from "../../../../../../../Wrap/Models/Models/InstrumentSelection/InstrumentSelection";
import { InstrumentSelections } from "../../../../../../../Wrap/Models/Models/InstrumentSelection/InstrumentSelections";
import { PensionContribution } from "../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContribution";
import { HierarchyIdHelper } from "../../../../../../Shared/Common/HierarchyIdHelper";
import {
    getISTPaymentAmount,
    initPensionInstrumentSelections,
    initWeighting,
} from "../../../../../../Shared/IST/Components/Shared/Utils";
import { InvestmentSelectionTool as IST } from "../../../../../../Shared/IST/InvestmentSelectionTool";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { AllocationTableItem } from "../../../../../../Shared/IST/Store/SelectedInvesmentStoreStatic";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { Action } from "../../../../../Actions/Action";
import { ClientAccountStore, TreasuredAssetsStore } from "../../../../../Stroes/Variable";
import { SubAccountTypeHelper } from "../../../Common/Helpers/SubAccountTypeHelper";
import { BankAccountOwnerPaymentTypeMap, PaymentType, TransferKeysMap } from "../../../Common/Resource/Contents";
import { IWizardDataModelTemplate } from "../../../SinglePaymentTransferWizard/Models/WizardDataModelTemplate";

interface IInvestmentSelectionToolProps extends IProcessComponentProps {
    type: SubAccountType;
    data: IWizardDataModelTemplate;
    treasuredAssetProductIds: number[];
    onTreasuredAssetsStoreChange: () => void;
    onChangeTreasuredAssets: (subAccountInstructionId, productId, value) => void;
}

interface IInvestmentSelectionToolState { }

export class InvestmentSelectionTool extends ProcessComponent<
    IInvestmentSelectionToolProps,
    IInvestmentSelectionToolState
> {
    constructor(props: any) {
        super(props);
        this.state = {};
        this.subscribeToStore(TreasuredAssetsStore, () => this.props.onTreasuredAssetsStoreChange());
    }

    componentDidMount() {
        super.componentDidMount();
        const subAccountInstructionId = HierarchyIdHelper.generateHierarchyId(this.props.data.HierarchyId).entityId;
        Action.getInstructionTreasuredAssetsProductIds(subAccountInstructionId);
    }

    private onInvestmentChanges = (changes) => {
        const preRetirement: AllocationTableItem[] = changes.Instruments.map((ele) => ({
            Instrument: ele.Instrument,
            Single: ele.Single,
            Regular: ele.Regular,
            ClientRegular: ele.ClientRegular,
            EmployerRegular: ele.EmployerRegular,
            ThirdPartyRegular: ele.ThirdPartyRegular,
            Transfer: ele.Transfer,
            isToogle: ele.isToogle,
            needToggle: ele.needToggle,
            underlying: ele.underlying,
            isTargetAllocation: ele.isTargetAllocation,
            modelPortfolioType: ele.modelPortfolioType,
            EmployerSalaryDeductionRegular: ele.EmployerSalaryDeductionRegular,
        }));
        const postRetirement = changes.Instruments.map((ele) => ({
            Instrument: ele.Instrument,
            Transfer: ele.PostTransfer || null,
            Single: initWeighting(),
            Regular: initWeighting(),
            ClientRegular: initWeighting(),
            EmployerRegular: initWeighting(),
            ThirdPartyRegular: initWeighting(),
        }));
        this.onDataChange(
            this.props.onChange,
            {
                PreRetirementInstrumentSelections: this.initInstrumentSelection(preRetirement),
                PostRetirementInstrumentSelections: this.initInstrumentSelection(postRetirement),
            },
            this.props.data,
            "Contribution"
        );
    };

    private initInstrumentSelection = (instruments: InstrumentSelection[]): InstrumentSelections => {
        return {
            Type: MoneyOrPercentageType.Percentage,
            Instruments: instruments,
            DistributionDividendPreference: DistributionDividendPreference.NotSpecified,
        };
    };

    public render(): JSX.Element {
        const { data, type, onChange, treasuredAssetProductIds, orderType, onOrderTypeChange } = this.props;

        const params = getQueryParameters();
        const paymentAmount = getISTPaymentAmount(data.Contribution, TransferKeysMap[type]);
        const accountsFromExisting = data.AllBankAccounts || [];
        const bankAccountInstructionsFiltered =
            (data.BankAccountInstructions || []).filter(
                (x) => BankAccountOwnerPaymentTypeMap[x.Owner].indexOf(PaymentType.Client) != -1
            ) || [];

        const bankAccounts = [...accountsFromExisting, ...bankAccountInstructionsFiltered];
        const modelData = JSON.parse(JSON.stringify(data?.Contribution));
        modelData.CashInstrument = data.CashInstrument;
        modelData.OwnerHierarchyId = data.OwnerHierarchyId;
        let instrumentSelection = data.Contribution?.InstrumentSelections;
        if (SubAccountTypeHelper.isOnPlatformPension(type)) {
            const preRetirement =
                (data?.Contribution as PensionContribution)?.PreRetirementInstrumentSelections?.Instruments || [];
            const postRetirement =
                (data?.Contribution as PensionContribution)?.PostRetirementInstrumentSelections?.Instruments || [];
            instrumentSelection = this.initInstrumentSelection([]);
            instrumentSelection.Instruments = initPensionInstrumentSelections(preRetirement, postRetirement);
        }
        const clientAccount = ClientAccountStore.getClientAccountInfo();

        return (
            <>
                <h2 className="mb-20">Investment selection</h2>
                <IST
                    withReRegistrationTransferIn={false}
                    data={instrumentSelection}
                    modelData={modelData}
                    onRef={(ref) => this.appendChild(ref)}
                    onChange={(changes) => {
                        if (changes.orderType) {
                            onOrderTypeChange(changes.orderType);
                        } else if (!SubAccountTypeHelper.isOnPlatformPension(type) || changes.AdviceGivenForOffPlatform != undefined) {
                            this.onDataChange(
                                (changes) => this.onDataChange(onChange, changes, data, "Contribution"),
                                changes,
                                data.Contribution,
                                "InstrumentSelections"
                            );
                        } else {
                            this.onInvestmentChanges(changes);
                        }
                    }}
                    showErrorMsgPopUp={(msg) => this.props.showErrorMsgPopUp(msg)}
                    type={ProcessType.EditRegular}
                    hiercharyId={params["subaccounthierarchyid"]}
                    claccountId={params["claccountid"]}
                    companyCode={clientAccount?.Company}
                    headAccountHierarchyId={clientAccount?.HierarchyId}
                    product={type}
                    paymentAmount={{ Amount: paymentAmount, Currency: CurrencyCode.GBP }}
                    bankAccounts={bankAccounts}
                    onBankChange={(bankInfo) => {
                        const banks = data.BankAccountInstructions.concat(bankInfo);
                        onChange({ BankAccountInstructions: banks });
                    }}
                    onDistributionDividendOptions={(changes) => {
                        this.onDataChange(
                            (changes) => this.onDataChange(onChange, changes, data, "Contribution"),
                            changes,
                            data.Contribution,
                            "DistributionDividendOptions"
                        );
                    }}
                    decisionMakerHierarchyId={params["subaccounthierarchyid"]}
                    accountAdviceType={data.SubAccountClientDetail?.AccountAdviceType}
                    accountServiceProposition={modelData.AccountServiceProposition}
                    onDecisionMakerChange={(v) => this.onDataChange(onChange, v, data, "Contribution")}
                    treasuredAssetsProductIds={treasuredAssetProductIds}
                    onChangeTreasuredAssets={(productId, value) => {
                        this.props.onChangeTreasuredAssets(data.HierarchyId, productId, value);
                    }}
                    orderType={orderType}
                />
            </>
        );
    }
}
